import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "../../../../axios";
import { fetchActivities } from "../../../../redux/slices/activities";
import {
  fetchCreateReview,
  fetchReviews,
} from "../../../../redux/slices/reviews";
import { pick } from "../../../../utils/localize";
import SideBar from "../../SideBar";
import Notification from "../components/Notification";
import Title from "../components/Title";
import Review from "./Review";

// ОТЗЫВЫ ДОБАВЛЯЮТСЯ СКРИНШОТАМИ.
//
// Раньше здесь была форма «имя / оценка / текст»: менеджер перепечатывал
// отзыв руками. Так на сайте оказывался не отзыв клиента, а его пересказ —
// доверия к такому тексту нет, потому что написать его мог кто угодно.
//
// Теперь в админку загружается сам скриншот: переписка в WhatsApp, сторис в
// Instagram, карточка в 2GIS. Видно, что это писал живой человек.
//
// Каждый выбранный файл становится отдельной карточкой на сайте. Подпись
// («Instagram», «WhatsApp») общая для загрузки — она говорит, откуда отзыв,
// и не притворяется именем автора.
//
// Отзыв кладётся в одно из двух мест — это выбирается перед загрузкой:
//   · общие — блок отзывов на главной и страница «Все отзывы»;
//   · тур   — отзыв показывается на странице этого тура, там, где человек
//             как раз решает, ехать ли: чужой опыт именно про этот маршрут
//             убеждает сильнее, чем отзыв о компании вообще.
const MAX_FILES = 20;
const GENERAL = "";

function ReviewGroup({ title, items }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-8">
      <div className="text-base font-semibold text-neutral-900">
        {title} <span className="text-neutral-400">({items.length})</span>
      </div>
      <div className="w-full max-w-7xl mx-auto flex flex-col gap-y-10 mt-4">
        {items.map((review) => (
          <Review key={review._id} data={review} />
        ))}
      </div>
    </div>
  );
}

export default function Reviews() {
  const dispatch = useDispatch();
  const { reviews } = useSelector((state) => state.reviews);
  const inputRef = useRef(null);

  const { activities } = useSelector((state) => state.activities);
  const [target, setTarget] = useState(GENERAL);
  const [source, setSource] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [notif, setNotif] = useState({ text: "", state: "success" });

  useEffect(() => {
    dispatch(fetchReviews());
    // список туров нужен для выбора, к какому из них привязать отзыв
    dispatch(fetchActivities());
  }, [dispatch]);

  const notify = (text, state) => {
    setNotif({ text, state });
    setShow(true);
    setTimeout(() => setShow(false), 3500);
  };

  // Файл за файлом: сначала картинка уходит в /upload, потом её адрес — в
  // отзыв. Один сорвавшийся файл не должен уронить всю пачку, поэтому
  // считаем удачные и неудачные отдельно и показываем итог одной строкой.
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_FILES);
    if (inputRef.current) inputRef.current.value = "";
    if (files.length === 0) return;

    setBusy(true);
    let added = 0;
    const failed = [];

    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("image", file);
        const { data } = await axios.post("/upload", fd);
        await dispatch(
          fetchCreateReview({
            name: source.trim(),
            stars: 0,
            comment: "",
            date,
            photos: [data.url],
            activityId: target,
          })
        ).unwrap();
        added += 1;
      } catch (error) {
        console.error(error);
        failed.push(file.name);
      }
    }

    setBusy(false);
    dispatch(fetchReviews());

    if (added && failed.length === 0) {
      notify(added === 1 ? "Скриншот добавлен" : `Добавлено скриншотов: ${added}`, "success");
    } else if (added) {
      notify(`Добавлено: ${added}. Не удалось загрузить: ${failed.join(", ")}`, "warn");
    } else {
      notify("Не удалось загрузить скриншоты", "error");
    }
  };

  const generalReviews = reviews.items.filter((review) => !review.activityId);
  const tourReviews = reviews.items.filter((review) => review.activityId);

  const inputCls =
    "mt-1 w-full rounded-md border-0 py-1.5 px-4 text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";
  const labelCls = "block text-sm font-medium text-neutral-700";

  return (
    <SideBar>
      <Notification
        show={show}
        setShow={setShow}
        text={notif.text}
        state={notif.state}
      />
      <Title text={"Отзывы"} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="py-4">
          <div className="text-lg font-medium">
            {`Отзывов на сайте: ${reviews.items.length}`}
            <span className="text-sm font-normal text-neutral-500">
              {` — общих ${generalReviews.length}, по турам ${tourReviews.length}`}
            </span>
          </div>

          <div className="mt-4 max-w-2xl rounded-xl border border-neutral-200 shadow-lg p-6 bg-white">
            <h2 className="text-base font-semibold text-neutral-900">
              Добавить скриншоты отзывов
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Переписка в WhatsApp, сторис в Instagram, карточка в 2GIS.
              Каждый файл станет отдельным отзывом на сайте.
            </p>

            <div className="mt-4">
              <label htmlFor="target" className={labelCls}>
                Куда добавить
              </label>
              <select
                id="target"
                className={inputCls}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value={GENERAL}>Общие отзывы (главная и «Все отзывы»)</option>
                {(activities.items || []).map((activity) => (
                  <option key={activity._id} value={activity._id}>
                    Тур: {pick(activity.name, "ru")}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4 flex flex-col md:flex-row gap-4">
              <div className="w-full">
                <label htmlFor="source" className={labelCls}>
                  Откуда отзыв{" "}
                  <span className="text-neutral-400">(необязательно)</span>
                </label>
                <input
                  id="source"
                  type="text"
                  className={inputCls}
                  placeholder="Instagram, WhatsApp, 2GIS"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                />
              </div>
              <div className="w-full md:max-w-[200px]">
                <label htmlFor="date" className={labelCls}>
                  Дата
                </label>
                <input
                  id="date"
                  type="date"
                  className={inputCls}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            </div>

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFiles}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="mt-5 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-500 disabled:opacity-60"
            >
              {busy ? "Загружаем…" : "Выбрать скриншоты"}
            </button>
            <p className="mt-2 text-xs text-neutral-400">
              PNG, JPG, WEBP или GIF. За раз — до {MAX_FILES} файлов.
            </p>
          </div>

          {/* Списком вперемешку было бы не видно, куда какой отзыв попал —
              а это единственное, что здесь можно перепутать. */}
          <ReviewGroup title="Общие отзывы" items={generalReviews} />
          {(activities.items || [])
            .map((activity) => ({
              activity,
              items: tourReviews.filter((r) => r.activityId === activity._id),
            }))
            .filter((group) => group.items.length > 0)
            .map(({ activity, items }) => (
              <ReviewGroup
                key={activity._id}
                title={`Тур: ${pick(activity.name, "ru")}`}
                items={items}
              />
            ))}
        </div>
      </div>
    </SideBar>
  );
}
